import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as Dialog from '@radix-ui/react-dialog';
import { 
  Upload, 
  X, 
  FileText, 
  Image as ImageIcon, 
  File, 
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { useDocumentStore, type Evidence } from '@/lib/stores';
import { useAuth } from '@/lib/auth/AuthProvider';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  weekKey: string;
}

interface UploadedFile {
  file: File;
  preview?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const ACCEPTED_FILE_TYPES = {
  'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
};

export function UploadModal({ isOpen, onClose, weekKey }: UploadModalProps) {
  const { user } = useAuth();
  const { addEvidence } = useDocumentStore();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [context, setContext] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: UploadedFile[] = acceptedFiles.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      status: 'pending' as const,
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const removeFile = (index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      // Revoke object URL if it exists
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadComplete(false);

    for (let i = 0; i < files.length; i++) {
      const uploadedFile = files[i];
      
      // Update status to uploading
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' as const } : f
      ));

      try {
        // Create evidence item
        const evidence: Evidence = {
          id: `${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
          userId: user?.localAccountId || '',
          weekKey,
          title: uploadedFile.file.name,
          description: context || `Uploaded file: ${uploadedFile.file.name}`,
          source: uploadedFile.file.type.startsWith('image/') 
            ? 'upload_screenshot' 
            : 'upload_document',
          category: 'accomplishment',
          confidence: 'medium',
          timestamp: Date.now(),
          capturedAt: Date.now(),
          people: [],
          projects: [],
          keywords: [],
          uploadedFileName: uploadedFile.file.name,
          // In a real implementation, you would upload to Azure Blob Storage
          // and get back a URL. For now, we'll use a placeholder.
          uploadedFileUrl: uploadedFile.preview || '',
        };

        // Add evidence to store
        await addEvidence(evidence);

        // Update status to success
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'success' as const } : f
        ));
      } catch (error) {
        // Update status to error
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { 
            ...f, 
            status: 'error' as const, 
            error: error instanceof Error ? error.message : 'Upload failed' 
          } : f
        ));
      }
    }

    setIsUploading(false);
    setUploadComplete(true);
  };

  const handleClose = () => {
    // Cleanup previews
    files.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setFiles([]);
    setContext('');
    setUploadComplete(false);
    onClose();
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <ImageIcon className="w-5 h-5" />;
    if (file.type.includes('pdf')) return <FileText className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const successCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-700">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              Add Evidence
            </Dialog.Title>
            <Dialog.Close asChild>
              <button 
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                onClick={handleClose}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </Dialog.Close>
          </div>

          <div className="p-6 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
            {/* Upload success message */}
            {uploadComplete && successCount > 0 && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-xl">
                <p className="text-green-700 dark:text-green-300 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Successfully uploaded {successCount} file{successCount !== 1 ? 's' : ''}!
                  {errorCount > 0 && ` (${errorCount} failed)`}
                </p>
              </div>
            )}

            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragActive
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-300 dark:border-dark-600 hover:border-purple-400 hover:bg-gray-50 dark:hover:bg-dark-700'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {isDragActive
                  ? 'Drop the files here...'
                  : 'Drag and drop files here, or click to browse'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Supports: Images (PNG, JPG), Documents (PDF, DOCX), Text files
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Maximum file size: 10MB
              </p>
            </div>

            {/* Selected files */}
            {files.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Selected Files ({files.length})
                </h4>
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {files.map((uploadedFile, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg"
                    >
                      {/* Preview or icon */}
                      {uploadedFile.preview ? (
                        <img
                          src={uploadedFile.preview}
                          alt={uploadedFile.file.name}
                          className="w-10 h-10 object-cover rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 dark:bg-dark-600 rounded flex items-center justify-center text-gray-500">
                          {getFileIcon(uploadedFile.file)}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {uploadedFile.file.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {(uploadedFile.file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>

                      {/* Status indicator */}
                      {uploadedFile.status === 'uploading' && (
                        <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                      )}
                      {uploadedFile.status === 'success' && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                      {uploadedFile.status === 'error' && (
                        <span title={uploadedFile.error}>
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        </span>
                      )}
                      {uploadedFile.status === 'pending' && (
                        <button
                          onClick={() => removeFile(index)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-dark-600 rounded"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Context input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Context (optional)
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Describe what these files show or represent..."
                className="w-full p-3 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-700/50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-600 rounded-lg transition-colors"
            >
              {uploadComplete ? 'Close' : 'Cancel'}
            </button>
            {!uploadComplete && (
              <button
                onClick={handleUpload}
                disabled={files.length === 0 || isUploading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {files.length} file{files.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
